import { useCallback, useState } from "react"

/* eslint-disable @typescript-eslint/no-explicit-any -- needed for type inference */

type Callback<P extends any[], R> = (...args: P) => Promise<R>

type AsyncFunctions = {
    [name: string]: Callback<any[], any>
}

type RemappedCallbackCollection<C extends AsyncFunctions> = {
    [P in keyof C]: (...args: Parameters<C[P]>) => ReturnType<C[P]>
}

export function useAsyncCallback<T extends AsyncFunctions>(
    fns: T,
    deps: unknown[] = [],
    defaultLoading = false
): {
    loading: boolean
    error: unknown | undefined
} & RemappedCallbackCollection<T> {
    const [loading, setLoading] = useState(defaultLoading)
    const [error, setError] = useState<unknown | undefined>(undefined)
    const collection = {} as RemappedCallbackCollection<T>
    for (const k in fns) {
        const fn = fns[k] as Callback<any[], any>
        collection[k] = useCallback(async (...args: any[]) => {
            setLoading(true)
            setError(undefined)
            try {
                return await fn(...args)
            } catch (err) {
                // this is so that we know that errors happen & the details of them, even if we do have handling in the UI
                console.error(err)
                setError(err)
            } finally {
                setLoading(false)
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, deps) as any
    }
    return {
        loading,
        error,
        ...collection,
    }
}
